import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area
} from 'recharts';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { 
  TrendingUp, 
  Clock, 
  Smartphone, 
  Monitor,
  Tablet,
  Download,
  Eye,
  Activity
} from 'lucide-react';


/**
 * AccessAnalytics - Shows detailed access patterns and usage analytics
 * 
 * Features:
 * - Download trends over time
 * - Hourly access patterns
 * - Device breakdown
 * - Popular access times
 * - User engagement metrics
 */
const AccessAnalytics = ({ data }) => {
  if (!data) {
    return (
      <div className="text-center py-4 text-gray-500">
        No access analytics data available
      </div>
    );
  }

  const chartConfig = {
    downloads: {
      label: "Downloads",
      color: "#3b82f6",
    },
    uniqueUsers: {
      label: "Unique Users",
      color: "#10b981",
    },
    accesses: {
      label: "Accesses",
      color: "#f59e0b",
    },
  };

  const deviceData = [
    { name: 'Desktop', value: data.deviceBreakdown.desktop, color: '#3b82f6', icon: Monitor },
    { name: 'Mobile', value: data.deviceBreakdown.mobile, color: '#10b981', icon: Smartphone },
    { name: 'Tablet', value: data.deviceBreakdown.tablet, color: '#f59e0b', icon: Tablet },
  ];

  const timeData = [
    { name: 'Morning (6AM-12PM)', value: data.popularTimes.morning, color: '#fbbf24' },
    { name: 'Afternoon (12PM-6PM)', value: data.popularTimes.afternoon, color: '#3b82f6' },
    { name: 'Evening (6PM-12AM)', value: data.popularTimes.evening, color: '#10b981' },
  ];

  return (
    <div className="space-y-6">
      {/* Download Trends */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <TrendingUp className="h-5 w-5" />
            <span>Download Trends</span>
          </CardTitle>
          <CardDescription>
            Downloads and unique user activity over time
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ChartContainer config={chartConfig} className="h-[400px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.downloadTrends}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="date" 
                  tick={{ fontSize: 12 }}
                  tickFormatter={(value) => {
                    const date = new Date(value);
                    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                  }}
                />
                <YAxis tick={{ fontSize: 12 }} />
                <ChartTooltip 
                  content={<ChartTooltipContent />}
                  labelFormatter={(value) => {
                    const date = new Date(value);
                    return date.toLocaleDateString('en-US', { 
                      month: 'long', 
                      day: 'numeric', 
                      year: 'numeric' 
                    });
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="downloads"
                  stackId="1"
                  stroke="var(--color-downloads)"
                  fill="var(--color-downloads)"
                  fillOpacity={0.6}
                />
                <Area
                  type="monotone"
                  dataKey="uniqueUsers"
                  stackId="2"
                  stroke="var(--color-uniqueUsers)"
                  fill="var(--color-uniqueUsers)"
                  fillOpacity={0.6}
                />
              </AreaChart>
            </ResponsiveContainer>
          </ChartContainer>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Hourly Access Patterns */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Clock className="h-5 w-5" />
              <span>Hourly Access Patterns</span>
            </CardTitle>
            <CardDescription>
              Material access by hour of day
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ChartContainer config={chartConfig} className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.accessPatterns}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="hour" 
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) => `${value}:00`}
                  />
                  <YAxis tick={{ fontSize: 12 }} />
                  <ChartTooltip 
                    content={<ChartTooltipContent />}
                    formatter={(value) => [value, 'Accesses']}
                    labelFormatter={(label) => `${label}:00`}
                  />
                  <Bar 
                    dataKey="accesses" 
                    fill="var(--color-accesses)"
                    radius={[2, 2, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </ChartContainer>
          </CardContent>
        </Card>

        {/* Device Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Monitor className="h-5 w-5" />
              <span>Device Breakdown</span>
            </CardTitle>
            <CardDescription>
              Access distribution by device type
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ChartContainer config={chartConfig} className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={deviceData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {deviceData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <ChartTooltip 
                    formatter={(value, name) => [`${value}%`, name]}
                  />
                </PieChart>
              </ResponsiveContainer>
            </ChartContainer>
            
            {/* Device Legend */}
            <div className="space-y-3 mt-4">
              {deviceData.map((device, index) => {
                const IconComponent = device.icon;
                return (
                  <div key={device.name} className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div 
                        className="w-4 h-4 rounded-full"
                        style={{ backgroundColor: device.color }}
                      />
                      <div className="flex items-center space-x-2">
                        <IconComponent className="h-4 w-4 text-gray-600" />
                        <span className="text-sm font-medium">{device.name}</span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-bold">{device.value}%</span>
                      <span className="text-xs text-gray-500">
                        (~{Math.round(device.value * 23.41)} users)
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Time of Day Analysis */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Activity className="h-5 w-5" />
            <span>Peak Usage Times</span>
          </CardTitle>
          <CardDescription>
            Material access patterns throughout the day
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {timeData.map((timeSlot, index) => (
              <div key={timeSlot.name} className="text-center p-4 border rounded-lg">
                <div className="flex items-center justify-center mb-3">
                  <div 
                    className="w-12 h-12 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: `${timeSlot.color}20` }}
                  >
                    <Clock 
                      className="h-6 w-6" 
                      style={{ color: timeSlot.color }}
                    />
                  </div>
                </div>
                <div 
                  className="text-2xl font-bold mb-2"
                  style={{ color: timeSlot.color }}
                >
                  {timeSlot.value}%
                </div>
                <p className="text-sm font-medium text-gray-700 mb-1">
                  {timeSlot.name}
                </p>
                <div className="w-full bg-gray-200 rounded-full h-2 mt-3">
                  <div 
                    className="h-2 rounded-full transition-all duration-300"
                    style={{ 
                      width: `${timeSlot.value * 2.5}%`,
                      backgroundColor: timeSlot.color 
                    }}
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Peak Times Insights */}
          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <Activity className="h-5 w-5 text-blue-600 mt-0.5" />
              </div>
              <div>
                <h4 className="font-medium text-blue-900 mb-2">Peak Usage Insights</h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• <strong>Afternoon (12PM-6PM)</strong> shows highest activity at 45%</li>
                  <li>• <strong>Morning sessions</strong> account for 35% of total access</li>
                  <li>• <strong>Evening access</strong> primarily from mobile devices (70%)</li>
                  <li>• <strong>Peak hour:</strong> 2:00 PM with average 180 downloads</li>
                </ul>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Access Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="text-center p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg">
          <div className="flex items-center justify-center mb-2">
            <Download className="h-6 w-6 text-blue-600" />
          </div>
          <div className="text-xl font-bold text-blue-600 mb-1">
            {Math.round(data.downloadTrends.reduce((sum, day) => sum + day.downloads, 0) / data.downloadTrends.length)}
          </div>
          <p className="text-xs text-blue-700 font-medium">Daily Average</p>
          <div className="text-xs text-blue-600 mt-1">Downloads per day</div>
        </div>

        <div className="text-center p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-lg">
          <div className="flex items-center justify-center mb-2">
            <Eye className="h-6 w-6 text-green-600" />
          </div>
          <div className="text-xl font-bold text-green-600 mb-1">
            {Math.max(...data.accessPatterns.map(p => p.accesses))}
          </div>
          <p className="text-xs text-green-700 font-medium">Peak Hour</p>
          <div className="text-xs text-green-600 mt-1">Max hourly access</div>
        </div>

        <div className="text-center p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg">
          <div className="flex items-center justify-center mb-2">
            <Monitor className="h-6 w-6 text-purple-600" />
          </div>
          <div className="text-xl font-bold text-purple-600 mb-1">
            {Math.max(...deviceData.map(d => d.value))}%
          </div>
          <p className="text-xs text-purple-700 font-medium">Primary Device</p>
          <div className="text-xs text-purple-600 mt-1">Desktop dominance</div>
        </div>

        <div className="text-center p-4 bg-gradient-to-br from-amber-50 to-amber-100 rounded-lg">
          <div className="flex items-center justify-center mb-2">
            <Clock className="h-6 w-6 text-amber-600" />
          </div>
          <div className="text-xl font-bold text-amber-600 mb-1">
            {Math.max(...timeData.map(t => t.value))}%
          </div>
          <p className="text-xs text-amber-700 font-medium">Peak Time</p>
          <div className="text-xs text-amber-600 mt-1">Afternoon activity</div>
        </div>
      </div>
    </div>
  );
};

export default AccessAnalytics;